import { useEffect, useState } from "react";
import {
  FORTRESS_ARCHIVED_CARDS,
  FORTRESS_CARDS_CONFIG,
  type FortressCard,
  type MatchHistoryFilter,
  type MatchHistoryItem,
  type MatchHistoryResponse,
} from "@fortress/shared";
import { listMatchHistory } from "./auth/lobbies";
import { FortressCardFace } from "./library/FortressCardFace";
import { PatternDiagram } from "./library/fortressPattern";

// The hub's Matches tab: one page of the account's finished matches
// (GET /games/history), filterable by status, each row expandable into the
// full result — winner, the win-fortress card, rounds and dates. The same row
// backs the home tab's "Recent results" (`RecentMatches`).

/** Resolve a persisted `winCardId` to its catalogue card for rendering. The
 * bundled catalogue is fine here — a listing needn't pin to the match's
 * catalogue version the way a resume does — but it must include the archive:
 * a match won with a card a later sheet dropped (BAM Exploder, Stab Pole
 * Hole, Church of Penguin) still names it. */
const CARD_BY_ID = new Map<string, FortressCard>(
  [
    ...(FORTRESS_CARDS_CONFIG.winCards as FortressCard[]),
    ...(FORTRESS_CARDS_CONFIG.regularCards as FortressCard[]),
    ...(FORTRESS_ARCHIVED_CARDS as FortressCard[]),
  ].map((card) => [card.id, card]),
);

const FILTER_LABELS: Record<MatchHistoryFilter, string> = {
  completed: "Completed",
  canceled: "Canceled",
  all: "All",
};

/** Result badge for one row. "Ended" covers games finished before the winner
 * was persisted (older saves) — there is a result, we just don't have it. */
function resultBadge(match: MatchHistoryItem): { label: string; mod: string } {
  if (match.status === "canceled") return { label: "Canceled", mod: "canceled" };
  if (match.youWon) return { label: "Won", mod: "won" };
  if (match.winnerName !== null) return { label: "Lost", mod: "lost" };
  return { label: "Ended", mod: "ended" };
}

function shortDate(iso: string | null): string | null {
  return iso ? new Date(iso).toLocaleDateString() : null;
}

function fullDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

/** 1 → "1st", 2 → "2nd", 3 → "3rd", 4 → "4th" … (11–13 → "th"). */
function ordinal(place: number): string {
  const tens = place % 100;
  if (tens >= 11 && tens <= 13) return `${place}th`;
  switch (place % 10) {
    case 1:
      return `${place}st`;
    case 2:
      return `${place}nd`;
    case 3:
      return `${place}rd`;
    default:
      return `${place}th`;
  }
}

/** Skill-rating movement: "+12" gained, "−8" lost, "±0" unmoved. Renders
 * nothing for a seat that wasn't rated (unranked match, guest, older save). */
function RatingDelta({ delta }: { delta: number | null }) {
  if (delta === null) return null;
  const rounded = Math.round(delta);
  const mod = rounded > 0 ? "up" : rounded < 0 ? "down" : "even";
  const text =
    rounded > 0 ? `+${rounded}` : rounded < 0 ? `−${-rounded}` : "±0";
  return (
    <span
      className={`lobby-hub-history-delta is-${mod}`}
      title="Skill rating change"
    >
      {text}
    </span>
  );
}

/**
 * Seat list as chips. When the row carries standings, chips are ordered by
 * final place and each carries its ordinal ("1st", "2nd", …) plus any rating
 * movement; 1st place keeps the trophy + accent. Rows without standings
 * (canceled games, results never recorded) fall back to plain seat-order
 * names with the winner matched by `winnerName`.
 */
function PlayerChips({ match }: { match: MatchHistoryItem }) {
  const standings = match.standings;
  if (standings && standings.length > 0) {
    return (
      <span className="lobby-hub-history-seats">
        {standings.map((s, i) => {
          const won = s.place === 1;
          return (
            <span
              key={`${s.name}-${i}`}
              className={`lobby-hub-history-seat${won ? " is-winner" : ""}`}
            >
              <span className="lobby-hub-history-place">
                {ordinal(s.place)}
              </span>
              {won && <span aria-hidden="true">🏆</span>}
              {s.name}
              <RatingDelta delta={s.ratingDelta} />
            </span>
          );
        })}
      </span>
    );
  }
  if (match.players.length === 0) {
    return <span className="lobby-hub-history-players">Unknown players</span>;
  }
  return (
    <span className="lobby-hub-history-seats">
      {match.players.map((name, i) => {
        const won = match.winnerName !== null && name === match.winnerName;
        return (
          <span
            key={`${name}-${i}`}
            className={`lobby-hub-history-seat${won ? " is-winner" : ""}`}
          >
            {won && <span aria-hidden="true">🏆</span>}
            {name}
          </span>
        );
      })}
    </span>
  );
}

/** The expanded row's standings list: place, name, rating movement, and the
 * rating each player left the match with (rated seats only). */
function StandingsList({ match }: { match: MatchHistoryItem }) {
  return (
    <ol className="lobby-hub-history-standings">
      {(match.standings ?? []).map((s, i) => (
        <li key={`${s.name}-${i}`}>
          <span className="lobby-hub-history-place">{ordinal(s.place)}</span>
          <span
            className={`lobby-hub-history-seat${s.place === 1 ? " is-winner" : ""}`}
          >
            {s.place === 1 && <span aria-hidden="true">🏆</span>}
            {s.name}
            {s.you ? " (you)" : ""}
          </span>
          <RatingDelta delta={s.ratingDelta} />
          {s.ratingAfter !== null && (
            <span
              className="lobby-hub-history-rating"
              title="Skill rating after this match"
            >
              → {Math.round(s.ratingAfter)}
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}

/** One finished match as a hub tile: the date (+ a quiet Ranked marker) with
 * the result tag pinned top-right, the standings under them, then the
 * win-fortress chip with "View match" anchored bottom-right for ended games;
 * expands into the full standings and facts. Shared by the Matches tab's
 * paged list and the home tab's recent results. */
function MatchRow({
  match,
  expanded,
  onToggle,
  onEnterMatch,
}: {
  match: MatchHistoryItem;
  expanded: boolean;
  onToggle: () => void;
  /** "View match" opens a finished game read-only via `/match/:gameId`. */
  onEnterMatch: (gameId: string) => void;
}) {
  const badge = resultBadge(match);
  const winCard = match.winCardId ? CARD_BY_ID.get(match.winCardId) : undefined;
  const finished = shortDate(match.finishedAt);
  return (
    <li className="lobby-hub-row lobby-hub-history-row">
      <div className="lobby-hub-history-head">
        <button
          type="button"
          className="lobby-hub-history-toggle"
          aria-expanded={expanded}
          onClick={onToggle}
        >
          <span className="lobby-hub-history-top">
            {finished && (
              <span className="lobby-hub-history-date">{finished}</span>
            )}
            {match.ranked && (
              <span
                className="lobby-hub-history-ranked"
                title={
                  match.poolName
                    ? `Ranked — ${match.poolName}`
                    : "Ranked match"
                }
              >
                Ranked
              </span>
            )}
            <span className={`lobby-hub-status is-${badge.mod}`}>
              {badge.label}
            </span>
          </span>
          <PlayerChips match={match} />
        </button>
        {(winCard || match.status === "ended") && (
          <div className="lobby-hub-history-foot">
            {winCard && (
              <span
                className="lobby-hub-history-fortress"
                title={`Won with ${winCard.name}`}
              >
                <PatternDiagram
                  pattern={winCard.pattern}
                  maxW={44}
                  maxH={30}
                  pad={3}
                  iconScale={0.56}
                  dark
                />
                <span className="lobby-hub-history-fortress-name">
                  {winCard.name}
                </span>
              </span>
            )}
            {match.status === "ended" && (
              <button
                type="button"
                className="lobby-hub-watch lobby-hub-history-view"
                title="Review the final board and result"
                onClick={() => onEnterMatch(match.gameId)}
              >
                View match
              </button>
            )}
          </div>
        )}
      </div>
      {expanded && (
        <div className="lobby-hub-history-details">
          <dl className="lobby-hub-history-facts">
            {match.standings && match.standings.length > 0 ? (
              <>
                <dt>Standings</dt>
                <dd>
                  <StandingsList match={match} />
                </dd>
              </>
            ) : (
              <>
                <dt>Players</dt>
                <dd>
                  {match.players.length > 0 ? (
                    <PlayerChips match={match} />
                  ) : (
                    "—"
                  )}
                </dd>
                <dt>Winner</dt>
                <dd>
                  {match.status === "canceled" ? (
                    "— (canceled)"
                  ) : match.winnerName ? (
                    <span className="lobby-hub-history-seat is-winner">
                      <span aria-hidden="true">🏆</span>
                      {match.winnerName}
                      {match.youWon ? " (you)" : ""}
                    </span>
                  ) : (
                    "Not recorded"
                  )}
                </dd>
              </>
            )}
            <dt>Ranked</dt>
            <dd>
              {match.ranked
                ? match.poolName
                  ? `Yes — ${match.poolName}`
                  : "Yes"
                : "No"}
            </dd>
            <dt>Rounds</dt>
            <dd>{match.roundNumber}</dd>
            <dt>Started</dt>
            <dd>{fullDate(match.startedAt)}</dd>
            <dt>{match.status === "canceled" ? "Canceled" : "Finished"}</dt>
            <dd>{fullDate(match.finishedAt)}</dd>
          </dl>
          {match.winCardId &&
            (winCard ? (
              <FortressCardFace card={winCard} style={{ maxWidth: 280 }} />
            ) : (
              <p className="lobby-hub-empty">Won with {match.winCardId}</p>
            ))}
        </div>
      )}
    </li>
  );
}

interface MatchHistoryProps {
  /** Enter a match by gameId (same handler the hub's flows use). */
  onEnterMatch: (gameId: string) => void;
}

export function MatchHistory({ onEnterMatch }: MatchHistoryProps) {
  const [status, setStatus] = useState<MatchHistoryFilter>("completed");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<MatchHistoryResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    setBusy(true);
    setError(null);
    listMatchHistory({ status, page })
      .then((res) => {
        if (!stale) setData(res);
      })
      .catch(() => {
        if (!stale) setError("Couldn't load your matches. Try again.");
      })
      .finally(() => {
        if (!stale) setBusy(false);
      });
    return () => {
      stale = true;
    };
  }, [status, page]);

  const onFilter = (next: MatchHistoryFilter) => {
    setStatus(next);
    setPage(1);
    setExpandedId(null);
  };

  const matches = data?.matches ?? [];
  const totalPages = data
    ? Math.max(1, Math.ceil(data.total / data.pageSize))
    : 1;

  return (
    <section className="lobby-hub-panel lobby-hub-list">
      <div className="lobby-hub-list-head">
        <h2>My matches</h2>
        <select
          className="auth-input lobby-hub-history-filter"
          aria-label="Filter matches by status"
          value={status}
          onChange={(e) => onFilter(e.target.value as MatchHistoryFilter)}
        >
          {(Object.keys(FILTER_LABELS) as MatchHistoryFilter[]).map((f) => (
            <option key={f} value={f}>
              {FILTER_LABELS[f]}
            </option>
          ))}
        </select>
      </div>
      {error && <p className="auth-error">{error}</p>}
      {matches.length === 0 && !busy && !error && (
        <p className="lobby-hub-empty">
          {status === "completed"
            ? "No completed matches yet."
            : "No matches here yet."}
        </p>
      )}
      <ul className="lobby-hub-rows">
        {matches.map((match) => (
          <MatchRow
            key={match.gameId}
            match={match}
            expanded={expandedId === match.gameId}
            onToggle={() =>
              setExpandedId(expandedId === match.gameId ? null : match.gameId)
            }
            onEnterMatch={onEnterMatch}
          />
        ))}
      </ul>
      {(totalPages > 1 || page > 1) && (
        <div className="lobby-hub-pager">
          <button
            type="button"
            className="lobby-hub-watch"
            disabled={busy || page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="lobby-hub-pager-label">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="lobby-hub-watch"
            disabled={busy || page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}

/** How many finished matches the home tab's "Recent results" shows. */
const RECENT_MATCHES = 3;

interface RecentMatchesProps {
  onEnterMatch: (gameId: string) => void;
  /** Switch the hub to the full Matches tab. */
  onSeeAll: () => void;
  /** Re-fetch trigger: changes when the account's in-progress set changes (a
   * match leaving it just ended). History isn't polled otherwise. */
  refreshKey: string;
}

/** The home tab's "Recent results": the account's three most recently
 * completed matches — who won, and with which fortress — each openable
 * read-only via "View match". */
export function RecentMatches({
  onEnterMatch,
  onSeeAll,
  refreshKey,
}: RecentMatchesProps) {
  const [matches, setMatches] = useState<MatchHistoryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    listMatchHistory({ status: "completed", page: 1 })
      .then((res) => {
        if (stale) return;
        setMatches(res.matches.slice(0, RECENT_MATCHES));
        setError(null);
      })
      .catch(() => {
        if (!stale) setError("Couldn't load your recent results.");
      });
    return () => {
      stale = true;
    };
  }, [refreshKey]);

  return (
    <section className="lobby-hub-panel lobby-hub-list">
      <div className="lobby-hub-list-head">
        <h2>Recent results</h2>
        <button type="button" className="auth-link" onClick={onSeeAll}>
          See all
        </button>
      </div>
      {error && <p className="auth-error">{error}</p>}
      {matches?.length === 0 && !error && (
        <p className="lobby-hub-empty">No completed matches yet.</p>
      )}
      {matches && matches.length > 0 && (
        <ul className="lobby-hub-rows">
          {matches.map((match) => (
            <MatchRow
              key={match.gameId}
              match={match}
              expanded={expandedId === match.gameId}
              onToggle={() =>
                setExpandedId(
                  expandedId === match.gameId ? null : match.gameId,
                )
              }
              onEnterMatch={onEnterMatch}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
